import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  Grid,
  Autocomplete,
  Chip,
  TextField,
} from "@mui/material";
import {
  Group,
  Image as KonvaImage,
  Layer,
  Line,
  Rect,
  Stage,
  Text,
  Path,
} from "react-konva";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { APP_DB, TRANSPARENCY } from "../../../constants/constants";
import { getHoverColor, hexToRGBA, isLoggedIn } from "../../../utility/utility";
import CloseIcon from "@mui/icons-material/Close";
import locationIcon from "../../../assets/location_icon.png";
import { getImageDimen } from "./GeneralView";
import ClearIcon from "@mui/icons-material/Clear";
import UserDetailCard from "./UserDetailCard";
import { HoverComponent } from "./HoverComponent";
import MapIcon from "@mui/icons-material/Map";
import PageHeader from "../../dashboard/common/PageHeader";
import useApiEp from "../../../hooks/useApiEp";
import useProductCategories from "../../dashboard/visitor/registration/hooks/useProductCategories";
// Ensure this import path matches your directory structure

export const getRelativePointerPosition = (node) => {
  const transform = node.getAbsoluteTransform().copy();
  transform.invert();
  const pos = node.getStage().getPointerPosition();
  return transform.point(pos);
};

export function getScales(
  containerWidth,
  containerHeight,
  imgWidth,
  imgHeight
) {
  const { width: originalWidth, height: originalHeight } = getImageDimen(
    imgWidth,
    imgHeight
  );
  const newWidth = Math.min(
    containerWidth,
    (containerHeight / imgHeight) * imgWidth
  );
  const newHeight = Math.min(
    containerHeight,
    (containerWidth / imgWidth) * imgHeight
  );
  const scaleX = newWidth / originalWidth;
  const scaleY = newHeight / originalHeight;
  return { scaleX, scaleY };
}

const cleanSubCatDisplay = (subCat) => {
  const match = subCat.match(/^(?:[^ -]+ - )?(.+)$/);
  return match ? match[1].trim() : subCat;
};

const HallView = () => {
  const { id } = useParams();
  const [query] = useSearchParams();
  const un = query.get("un");
  const navigate = useNavigate();

  const { data: Halls } = useApiEp(`/user/get-hall-view?id=${id}&pb=${APP_DB}`);
  const { data: Mdata } = useProductCategories();

  // --- Filter States ---
  const [selectedExhibitor, setSelectedExhibitor] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectCountry, setSelectCountry] = useState("");

  const categoryKey =
    Mdata?.type === "triple"
      ? "SubCategory"
      : Mdata?.type === "double"
      ? "Category"
      : "MainCategory";

  // Use the same event category level as Explore Exhibitors.
  const uniqueCategories = useMemo(() => {
    if (!Mdata?.categories) return [];
    return [
      ...new Set(
        Mdata.categories
          .map((item) => item[categoryKey])
          .filter(Boolean)
      ),
    ].sort();
  }, [Mdata, categoryKey]);

  // Unique Exhibitors from Hall view
  const exhibitorOptions = useMemo(() => {
    if (!Halls?.exhibitors || !Array.isArray(Halls.exhibitors)) return [];
    const set = new Set();
    Halls.exhibitors.forEach((ex) => {
      const name =
        ex?.companyShortName?.value ||
        ex?.companyName?.value ||
        ex?.company_name ||
        ex?.name;
      if (name) set.add(name);
    });
    return Array.from(set).sort();
  }, [Halls]);

  // Standard states
  const [selectedChip, setSelectedChip] = useState(null);
  const [image, setImage] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [stageScale, setStageScale] = useState(1);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [newHalls, setNewHalls] = useState();
  const hoverTimeout = useRef(null);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const stageRef = useRef(null);
  const [hoverData, setHoverData] = useState({
    show: false,
    hall: null,
    stand: null,
    position: { x: 0, y: 0, width: 0, height: 0 },
  });

  const [lastDist, setLastDist] = useState(0);
  const [lastCenter, setLastCenter] = useState(null);
  const [countries, setCountries] = useState([]);
  const [locationImg, setLocationImg] = useState(null);
  const [filteredExhibitors, setFilteredExhibitors] = useState();
  const [currentExhibitor, setCurrentExhibitor] = useState();

  useEffect(() => {
    const img = new window.Image();
    img.src = locationIcon;
    img.onload = () => setLocationImg(img);
  }, []);

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate("/login");
    }
  }, []);

  useEffect(() => {
    setStagePos({ x: 0, y: 0 });
    setStageScale(1);
  }, [id]);

  // --- Filtering Exhibitors ---
  useEffect(() => {
    if (
      !selectCountry &&
      !selectedExhibitor &&
      (!selectedCategories || selectedCategories.length === 0)
    ) {
      setFilteredExhibitors([]);
      return;
    }
    const filtered = Halls?.exhibitors?.filter((exhibitor) => {
      let matches = true;
      if (selectCountry) {
        matches = matches && exhibitor?.country?.value?.name === selectCountry;
      }
      if (selectedExhibitor) {
        const exName =
          exhibitor?.companyShortName?.value ||
          exhibitor?.companyName?.value ||
          exhibitor?.company_name ||
          exhibitor?.name ||
          "";
        matches =
          matches && exName.toLowerCase() === selectedExhibitor.toLowerCase();
      }
      if (selectedCategories && selectedCategories.length > 0) {
        const pCats = exhibitor.productCategories?.value || [];
        const hasMatch = selectedCategories.some((selectedCat) =>
          pCats.some((category) => category?.[categoryKey] === selectedCat)
        );
        matches = matches && hasMatch;
      }
      return matches;
    });
    setFilteredExhibitors(filtered);
  }, [selectCountry, selectedExhibitor, selectedCategories, Halls, categoryKey]);

  useEffect(() => {
    if (un && Halls) {
      const exhibitor = Halls.exhibitors.find((item) => item.username === un);
      setFilteredExhibitors([exhibitor]);
    }
  }, [un, Halls]);

  const isHallHighlighted = (hall) => {
    return filteredExhibitors?.some(
      (exhibitor) =>
        exhibitor.hallNumber.value === Halls.hall.name &&
        exhibitor.standNumber.value === hall.name
    );
  };

  // Touch and Zoom Logic
  const getDistance = (touch1, touch2) => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getCenter = (touch1, touch2) => {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2,
    };
  };

  const handleTouchMove = (e) => {
    e.evt.preventDefault();
    const stage = stageRef.current;
    const touches = e.evt.touches;
    if (touches.length === 2) {
      const newDist = getDistance(touches[0], touches[1]);
      if (lastDist) {
        const scaleRatio = newDist / lastDist;
        const newScale = stageScale * scaleRatio;
        const center = getCenter(touches[0], touches[1]);
        if (lastCenter) {
          const deltaX = center.x - lastCenter.x;
          const deltaY = center.y - lastCenter.y;
          const newPos = {
            x: stage.x() - deltaX * scaleRatio,
            y: stage.y() - deltaY * scaleRatio,
          };
          stage.scale({ x: newScale, y: newScale });
          stage.position(newPos);
          stage.batchDraw();
        }
        setLastCenter(center);
        setStageScale(newScale);
      }
      setLastDist(newDist);
    }
  };

  const handleTouchEnd = () => {
    setLastDist(0);
    setLastCenter(null);
  };

  const handleWheel = (e) => {
    e.evt.preventDefault();
    const scaleBy = 1.09;
    const stage = e.target.getStage();
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();
    const newScale = e.evt.deltaY > 0 ? oldScale * scaleBy : oldScale / scaleBy;
    const minScale = 0.5;
    const maxScale = 3;
    const finalScale = Math.max(minScale, Math.min(maxScale, newScale));
    setStageScale(finalScale);
    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };
    const newPos = {
      x: pointer.x - mousePointTo.x * finalScale,
      y: pointer.y - mousePointTo.y * finalScale,
    };
    setStagePos(newPos);
  };

  // Hover and Map Events
  const handleMouseEnter = (event, hall) => {
    clearTimeout(hoverTimeout.current);
    setHoverData((prevData) => {
      if (!prevData.show || prevData.hall !== hall.hall) {
        return {
          show: true,
          hall: Halls.hall,
          stand: hall,
          position: {
            x: hall.shape.shape.x,
            y: hall.shape.shape.y,
            width: hall.shape.shape.width,
            height: hall.shape.shape.height,
          },
        };
      }
      return prevData;
    });
  };

  const handleMouseLeave = () => {
    hoverTimeout.current = setTimeout(() => {
      setHoverData((prev) => ({ ...prev, show: false }));
    }, 100);
  };

  useEffect(() => {
    return () => clearTimeout(hoverTimeout.current);
  }, []);

  useEffect(() => {
    if (Halls) {
      if (Halls?.exhibitors) {
        const uniqueCountryNames = [
          ...new Set(
            Halls.exhibitors
              .map((exhibitor) => exhibitor.country?.value?.name)
              .filter((name) => name)
          ),
        ].sort();
        setCountries(uniqueCountryNames);
      }
      setSelectedChip(Halls.hall.name);
      const img = new window.Image();
      img.src = Halls.hall.image;
      img.onload = () => {
        setImage({
          img: img,
          width: img.width,
          height: img.height,
        });
      };
    }
  }, [Halls]);

  useEffect(() => {
    if (Halls && image && containerRef) {
      const containerWidth = containerRef.current.offsetWidth;
      setDimensions({
        width: containerWidth,
        height: 560,
      });
      const { scaleX, scaleY } = getScales(
        containerWidth,
        560,
        image.width,
        image.height
      );
      const imageX =
        (containerWidth -
          Math.min(containerWidth, (560 / image.height) * image.width)) /
        2;
      const imageY =
        (560 - Math.min(560, (containerWidth / image.width) * image.height)) /
        2;
      const halls = Halls.stands.map((hall) => {
        if (hall.shape.type === "Rectangle") {
          return {
            ...hall,
            shape: {
              ...hall.shape,
              shape: {
                ...hall.shape.shape,
                x: hall.shape.shape.x * scaleX + imageX,
                y: hall.shape.shape.y * scaleY + imageY,
                width: hall.shape.shape.width * scaleX,
                height: hall.shape.shape.height * scaleY,
              },
            },
          };
        } else if (hall.shape.type === "Polygon") {
          return {
            ...hall,
            shape: {
              ...hall.shape,
              shape: hall.shape.shape.map((point, index) =>
                index % 2 === 0
                  ? point * scaleX + imageX
                  : point * scaleY + imageY
              ),
            },
          };
        }
        return hall;
      });
      setNewHalls(halls);
    }
  }, [Halls, image, containerRef]);

  const onViewExhibitor = (hall, stand) => {
    const exhibitor = Halls.exhibitors.find(
      (item) =>
        item.hallNumber.value === hall.name &&
        item.standNumber.value === stand.name
    );
    setCurrentExhibitor(exhibitor);
    setIsOpen(true);
  };

  function truncateText(text, maxWidth, fontSize) {
    const exhibitor = Halls?.exhibitors?.find(
      (d) =>
        d.hallNumber.value === Halls.hall.name && d.standNumber.value === text
    );
    const name = exhibitor ? exhibitor.companyName.value : "";
    const approxCharWidth = fontSize * 0.6;
    const maxChars = Math.floor(maxWidth / approxCharWidth);
    if (name.length > maxChars) {
      return name.substring(0, maxChars - 3) + "..";
    }
    return name;
  }

  return (
    <Box maxWidth={1280} m="8px auto">
      <Drawer
        anchor="left"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        sx={{
          width: "300px",
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: "300px",
            boxSizing: "border-box",
          },
        }}
      >
        <IconButton
          size="small"
          onClick={() => setIsOpen(false)}
          sx={{ position: "absolute", top: 8, right: 8, zIndex: 20 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            height: "100%",
            p: 2,
            backgroundColor: "#f8fafc",
          }}
        >
          {currentExhibitor ? (
            <UserDetailCard user={currentExhibitor} />
          ) : (
            <Typography color="text.secondary" textAlign="center">
              No exhibitor is assigned to this stand.
            </Typography>
          )}
        </Box>
      </Drawer>

      <Box
        sx={{
          p: { xs: 2, md: 4 },
          backgroundColor: "#f8fafc",
          minHeight: "100vh",
        }}
      >
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid #eef2f6",
            boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
            p: 3,
            mb: 3,
          }}
        >
          <Stack gap={3}>
            {/* Header section */}
            <PageHeader
              title={`Hall View - ${selectedChip || id}`}
              subtitle="Interactive floorplan map and booth navigation"
              icon={MapIcon}
            />

            {/* Filter Section - 4-column Grid */}
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Hall</InputLabel>
                  <Select
                    value={selectedChip || ""}
                    onChange={(event) => {
                      const hallName = event.target.value;
                      if (id !== hallName) {
                        setImage(null);
                        navigate(`/hall-view/${hallName}`, { replace: true });
                      }
                    }}
                    label="Select Hall"
                    sx={{ borderRadius: "12px", backgroundColor: "#f8fafc" }}
                  >
                    <MenuItem value="" disabled>
                      Select Hall
                    </MenuItem>
                    {[...(Halls?.halls || [])]
                      .sort((a, b) => a.name.localeCompare(b.name, undefined, {
                        numeric: true,
                        sensitivity: "base",
                      }))
                      .map((hall, i) => (
                        <MenuItem
                          key={i}
                          value={hall.name}
                          sx={{
                            fontWeight:
                              selectedChip === hall.name ? "bold" : "normal",
                          }}
                        >
                          {hall.name}
                        </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  size="small"
                  options={exhibitorOptions}
                  value={selectedExhibitor}
                  onChange={(e, newValue) => setSelectedExhibitor(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Exhibitor"
                      placeholder="Search Exhibitor..."
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "#f8fafc",
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  multiple
                  size="small"
                  options={uniqueCategories}
                  value={selectedCategories}
                  onChange={(e, newValue) => setSelectedCategories(newValue)}
                  limitTags={1}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Categories"
                      placeholder="Filter Categories..."
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "#f8fafc",
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Country</InputLabel>
                  <Select
                    value={selectCountry || ""}
                    onChange={(event) => setSelectCountry(event.target.value)}
                    label="Select Country"
                    sx={{ borderRadius: "12px", backgroundColor: "#f8fafc" }}
                    endAdornment={
                      selectCountry && (
                        <InputAdornment position="end" sx={{ mr: 2 }}>
                          <IconButton
                            size="small"
                            onClick={() => setSelectCountry("")}
                            edge="end"
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  >
                    <MenuItem value="" disabled>
                      Select Country
                    </MenuItem>
                    {countries.map((country, index) => (
                      <MenuItem key={index} value={country}>
                        {country}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Stack>
        </Card>

        {/* Map UI */}
        <Card ref={containerRef} sx={{ mt: 1 }} variant="outlined">
          {hoverData.show && (
            <HoverComponent
              allExhibitors={Halls?.exhibitors}
              hall={hoverData.hall}
              stand={hoverData.stand}
            />
          )}
          {image ? (
            <Stage
              width={dimensions.width}
              height={dimensions.height}
              scaleX={stageScale}
              scaleY={stageScale}
              ref={stageRef}
              x={stagePos.x}
              y={stagePos.y}
              onWheel={handleWheel}
              draggable={true}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <Layer>
                <KonvaImage
                  image={image.img}
                  width={Math.min(
                    dimensions.width,
                    (dimensions.height / image.height) * image.width
                  )}
                  height={Math.min(
                    dimensions.height,
                    (dimensions.width / image.width) * image.height
                  )}
                  x={
                    (dimensions.width -
                      Math.min(
                        dimensions.width,
                        (dimensions.height / image.height) * image.width
                      )) /
                    2
                  }
                  y={
                    (dimensions.height -
                      Math.min(
                        dimensions.height,
                        (dimensions.width / image.width) * image.height
                      )) /
                    2
                  }
                />
                {newHalls
                  ?.filter((hall) => hall.shape.type === "Polygon")
                  .map((hall) => (
                    <Group key={hall.name}>
                      <Line
                        points={hall.shape.shape}
                        fill={hexToRGBA(hall.color, TRANSPARENCY)}
                        stroke={hexToRGBA(hall.color, TRANSPARENCY)}
                        strokeWidth={hall.strokeWidth}
                        closed={true}
                        onMouseEnter={(e) => {
                          const shape = e.target;
                          shape.fill(getHoverColor(hall.color));
                          shape.getLayer().batchDraw();
                          handleMouseEnter(e, hall);
                        }}
                        onMouseLeave={(e) => {
                          const shape = e.target;
                          shape.fill(hexToRGBA(hall.color, TRANSPARENCY));
                          shape.getLayer().batchDraw();
                          handleMouseLeave();
                        }}
                        onClick={() => onViewExhibitor(Halls.hall, hall)}
                        onTap={() => onViewExhibitor(Halls.hall, hall)}
                      />
                    </Group>
                  ))}
                {newHalls
                  ?.filter((hall) => hall.shape.type === "Rectangle")
                  .map((hall) => (
                    <Group key={hall.name}>
                      <Rect
                        x={hall.shape.shape.x}
                        y={hall.shape.shape.y}
                        width={hall.shape.shape.width}
                        height={hall.shape.shape.height}
                        fill={hexToRGBA(hall.color, TRANSPARENCY)}
                        stroke={hexToRGBA(hall.color, TRANSPARENCY)}
                        strokeWidth={hall.strokeWidth}
                        onClick={() => onViewExhibitor(Halls.hall, hall)}
                        onTap={() => onViewExhibitor(Halls.hall, hall)}
                        onMouseEnter={(e) => {
                          const shape = e.target;
                          shape.fill(getHoverColor(hall.color));
                          shape.getLayer().batchDraw();
                          handleMouseEnter(e, hall);
                        }}
                        onMouseLeave={(e) => {
                          const shape = e.target;
                          shape.fill(hexToRGBA(hall.color, TRANSPARENCY));
                          shape.getLayer().batchDraw();
                          handleMouseLeave();
                        }}
                      />
                      {isHallHighlighted(hall) && (
                        <Path
                          x={
                            hall.shape.shape.x + hall.shape.shape.width / 2 - 12
                          }
                          y={
                            hall.shape.shape.y +
                            hall.shape.shape.height / 2 -
                            24
                          }
                          data="M12 0C7.58 0 4 3.58 4 8c0 5.25 8 16 8 16s8-10.75 8-16c0-4.42-3.58-8-8-8zm0 11.5c-1.93 0-3.5-1.57-3.5-3.5S10.07 4.5 12 4.5 15.5 6.07 15.5 8 13.93 11.5 12 11.5z"
                          fill="#e9ff00ff"
                          shadowColor="rgba(0,0,0,0.3)"
                          shadowBlur={6}
                          shadowOffset={{ x: 0, y: 3 }}
                          shadowOpacity={0.8}
                        />
                      )}
                      <Text
                        text={truncateText(
                          hall.name,
                          hall.shape.shape.width,
                          8
                        )}
                        x={hall.shape.shape.x + 4}
                        y={hall.shape.shape.y + hall.shape.shape.height / 2 - 4}
                        fill={"black"}
                        fontSize={8}
                        fontWeight="bold"
                      />
                    </Group>
                  ))}
              </Layer>
            </Stage>
          ) : (
            <Box sx={{ p: 3, width: "100%", height: "100%" }}>Loading ...</Box>
          )}
        </Card>
      </Box>
    </Box>
  );
};

export default HallView;
